import { AppContext } from "@/services/AppContext/appContext";
import { ShortAppUser } from "@/types";
import { useCallback, useContext, useMemo } from "react";

export const useUser = () => {
  const { allAppUsers } = useContext(AppContext);

  // useCallback ensures the function is memoized and only recreated when dependencies change
  const getUserById = useCallback(
    (id: number | string): ShortAppUser | undefined => {
      try {
        if (typeof id === "string") {
          id = Number(id); // Using Number to convert string to number
        }

        if (isNaN(id)) {
          throw new Error("Invalid ID: " + id);
        }

        return allAppUsers.find((user) => user.id === id);
      } catch (error) {
        console.error("Error fetching user by ID:", error);
        return undefined;
      }
    },
    [allAppUsers]
  );

  // useCallback ensures the function is memoized and only recreated when dependencies change
  const getUserFullNameByID = useCallback(
    (id: number | string): string => {
      try {
        const user = getUserById(id);
        return user ? user.first_name + " " + user.surname : "Unknown User";
      } catch (error) {
        console.error("Error fetching user full name by ID:", error);
        return "Unknown User";
      }
    },
    [getUserById]
  );

  const getUserProfileImageByID = useCallback(
    (id: number | string): string => {
      try {
        const user = getUserById(id);
        return user ? user.profile_picture : "";
      } catch (error) {
        console.error("Error fetching user profile image by ID:", error);
        return "Unknown User";
      }
    },
    [getUserById]
  );

  // useMemo ensures the returned object is memoized and only recreated when dependencies change
  return useMemo(
    () => ({
      getUserById,
      getUserFullNameByID,
      getUserProfileImageByID,
    }),
    [getUserById, getUserFullNameByID, getUserProfileImageByID]
  );
};
